import React, { useCallback } from 'react'

import { t } from 'i18next'
import { useFormContext, useFieldArray } from 'react-hook-form'

import { useMutationCadastrarCliente } from '@/features/clientes/hooks'
import { useMutationAtualizarCliente } from '@/features/clientes/hooks/use-atualizar-cliente'
import { useSuspenseNacionalidade } from '@/features/clientes/hooks/use-suspense-nacionalidade'
import { useSuspenseProfissoes } from '@/features/clientes/hooks/use-suspense-profissoes'
import {
  CardComprador,
  CardCompradorHeader,
  CardCompradorContent,
  CardCompradorInfo,
  ListaProcuradores,
  ListaVinculosComprador,
} from '@/features/proposta/components'
import { obterIdPorTipo, toClienteDto } from '@/features/proposta/entities/Comprador/dto/mapper-comprador-pf-api'
import { toContaDto } from '@/features/proposta/entities/Comprador/dto/mapper-comprador-pj-api'
import { CompradorListagemCommandFactory } from '@/features/proposta/entities/Comprador/form-to-listagem/comprador-listagem-command-factory'
import { obterTodosCompradoresPFEPJ } from '@/features/proposta/entities/Comprador/merge-comprador-pj-pf'
import {
  obterOpcoesCadastroComprador,
  type PersonType,
} from '@/features/proposta/entities/Comprador/opcoes-comprador-menu'
import { ApiCommandFactory } from '@/features/proposta/entities/Comprador/sincronizar-comprador-command'
import { transformarClienteParaEmailDocumento } from '@/features/proposta/entities/Comprador/transformar-emails-clientes'
import { useStepValidationStrategy } from '@/features/proposta/hooks'
import { useMutationAtualizarPessoaJuridica } from '@/features/proposta/hooks/use-atualizar-pj'
import { useMutationCadastrarPessoaJuridica } from '@/features/proposta/hooks/use-criar-pj'
import { useOrquestrarCriacaoEdicao } from '@/features/proposta/hooks/use-orquestrar-criacao-edicao'
import {
  StepPropostaContent,
  StepPropostaFooter,
  StepPropostaHeader,
  StepPropostaHeaderAction,
  StepPropostaHeaderContent,
  StepPropostaHeaderDescription,
  StepPropostaHeaderTitle,
  StepPropostaRoot,
} from '@/features/proposta/layouts'
import type { TPropostaSchemaInput } from '@/features/proposta/schemas'
import type {
  CompradorPessoaFisicaStepSchemaOutput,
  CompradorVinculoStepSchemaOutput,
} from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import type { CompradorPessoaJuridicaSchemaOutput } from '@/features/proposta/schemas/compradores/comprador-pj.schema'
import { CadastroCompradorPF } from '@/pages/proposta/etapa-comprador/cadastro-comprador/cadastro-comprador-pessoa-fisica'
import { CadastroCompradorPJ } from '@/pages/proposta/etapa-comprador/cadastro-comprador/cadastro-comprador-pessoa-juridica'
import { CadastroMaePaiFluxoIndependente } from '@/pages/proposta/etapa-comprador/cadastro-comprador/cadastro-mae-pai-fluxo-independente'
import { CadastroProcuradorFluxoIndependente } from '@/pages/proposta/etapa-comprador/cadastro-comprador/cadastro-procurador-fluxo-independente'
import { CadastroRepresentanteLegalFluxoIndependente } from '@/pages/proposta/etapa-comprador/cadastro-comprador/cadastro-representante-legal-fluxo-independente'
import { useExcluirComprador } from '@/pages/proposta/etapa-comprador/use-excluir-comprador'
import { Icon } from '@/shared/components/icon'
import { Render } from '@/shared/components/render-if'
import SelectPopover from '@/shared/components/select-popover'
import { Button } from '@/shared/components/ui'
import type { AxiosErrorMap } from '@/shared/lib/http/axios-adapter'
import { showToast } from '@/shared/lib/sonner'

const CadastroCompradoresEmpty: React.FC = () => {
  return (
    <div className="mb-6 flex min-h-96 w-full flex-col items-center justify-center">
      <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-orange-100">
        <Icon name="blocks" className="h-10 w-10 text-orange-900" />
      </div>
      <h2 className="text-md mb-2 font-semibold text-neutral-700">
        {t('proposta:cadastro_compradores.empty_state.nada_encontrado')}
      </h2>
      <p className="font-nunito mb-6 text-sm text-neutral-800">
        {t('proposta:cadastro_compradores.empty_state.cadastre_comprador')}
      </p>
    </div>
  )
}

export const EtapaComprador = () => {
  const {
    watch,
    setValue,
    trigger,
    control,
    getValues,
    formState: { errors },
  } = useFormContext<TPropostaSchemaInput>()
  const { validateStep } = useStepValidationStrategy(trigger)

  const { data: nacionalidades } = useSuspenseNacionalidade()
  const { data: profissoes } = useSuspenseProfissoes()
  const clienteAtendimento = watch('informacoesIniciais.clienteAtendimento')

  const currentStep = watch('step')

  const {
    fields: clientes,
    append: appendCliente,
    update: updateCliente,
    remove: removeCliente,
  } = useFieldArray({ name: 'cadastroCompradores.clientes', control, keyName: 'idField' })

  const {
    fields: compradores,
    append: appendComprador,
    update: updateComprador,
    remove: removeComprador,
  } = useFieldArray({ name: 'cadastroCompradores.compradores', control, keyName: 'idField' })

  const {
    fields: procuradores,
    append: appendProcurador,
    update: updateProcurador,
    remove: removeProcurador,
  } = useFieldArray({ name: 'cadastroCompradores.procuradores', control, keyName: 'idField' })

  const {
    fields: clientesJuridicos,
    append: appendClienteJuridico,
    update: updateClienteJuridico,
    remove: removeClienteJuridico,
  } = useFieldArray({
    name: 'cadastroCompradores.clientesJuridicos',
    control,
    keyName: 'idField',
  })

  const { handleOnExcluirComprador, obterProcuradoresDoComprador } = useExcluirComprador(
    { clientes, compradores, procuradores, clientesJuridicos },
    { removeCliente, removeComprador, removeProcurador, removeClienteJuridico, updateComprador, updateProcurador },
  )

  const compradoresComum = obterTodosCompradoresPFEPJ(compradores, clientes, clientesJuridicos)
  const isEmpty = compradoresComum.length === 0

  const { clienteToEdit, flowKey, handleCloseStepsComprador, handleEdit, mode, openCreate } =
    useOrquestrarCriacaoEdicao(compradores, clientes, clientesJuridicos)

  const { mutateAsync: atualizarCliente } = useMutationAtualizarCliente()
  const { mutateAsync: cadastrarCliente } = useMutationCadastrarCliente()
  const { mutateAsync: cadastrarPessoaJuridica } = useMutationCadastrarPessoaJuridica()
  const { mutateAsync: atualizarPessoaJuridica } = useMutationAtualizarPessoaJuridica()

  const handleErrorApi = (error?: AxiosErrorMap) => {
    if (error?.response?.data.some((err) => err.code === 'DOCUMENT_ALREADY_EXISTS')) {
      showToast({
        message: t('proposta:cadastro_compradores.erro.ja_cadastrado'),
        variant: 'error',
      })
      return
    }
    showToast({
      message: t('errors_gerais.padrao', { ns: 'common' }),
      variant: 'error',
    })
  }

  const obterClientesAtualizados = () => getValues('cadastroCompradores.clientes')
  const obterCompradoresAtualizados = () => getValues('cadastroCompradores.compradores')
  const obterProcuradoresAtualizados = () => getValues('cadastroCompradores.procuradores')
  const obterClientesJuridicosAtualizados = () => getValues('cadastroCompradores.clientesJuridicos')

  const handleSubmit = async (
    comprador:
      | CompradorPessoaFisicaStepSchemaOutput
      | CompradorPessoaJuridicaSchemaOutput
      | CompradorVinculoStepSchemaOutput,
    isDirty: boolean,
  ) => {
    const deps = {
      adicionarCliente: appendCliente,
      atualizarCliente: updateCliente,
      adicionarClienteJuridico: appendClienteJuridico,
      atualizarClienteJuridico: updateClienteJuridico,
      adicionarComprador: appendComprador,
      atualizarComprador: updateComprador,
      adicionarProcurador: appendProcurador,
      atualizarProcurador: updateProcurador,
      removeCliente: removeCliente,
    }

    const apiCmd = ApiCommandFactory.criar(comprador, isDirty, {
      fisico: {
        create: (formValue, papel) => {
          const dto = toClienteDto(formValue, papel)
          return cadastrarCliente(dto, { onError: handleErrorApi })
        },
        update: (formValue, papel) => {
          const dto = toClienteDto(formValue, papel)
          const id = obterIdPorTipo(papel, formValue)
          return atualizarCliente({ id, ...dto }, { onError: handleErrorApi })
        },
      },
      juridico: {
        create: (formValue) => {
          const dto = toContaDto(formValue)
          return cadastrarPessoaJuridica(dto)
        },
        update: (formValue) => {
          const dto = toContaDto(formValue)
          return atualizarPessoaJuridica({ ...dto, id: formValue.dados.id! })
        },
      },
    })

    const compradorSalvo = await apiCmd.executar()
    const compradoresParaAdicionarVinculo =
      comprador.tipo === 'Procurador' ||
      comprador.tipo === 'Representante' ||
      (comprador.tipo === 'PessoaFisica' && comprador.fluxoIndependente === 'MaePai')

    const isEditing = comprador.mode === 'edit' && clienteToEdit

    const idToUpdate = isEditing ? clienteToEdit.id : null
    const listaCmd =
      isEditing || compradoresParaAdicionarVinculo
        ? CompradorListagemCommandFactory.atualizar(
            compradorSalvo,
            deps,
            obterCompradoresAtualizados,
            obterClientesAtualizados,
            obterClientesJuridicosAtualizados,
            obterProcuradoresAtualizados,
            idToUpdate,
          )
        : CompradorListagemCommandFactory.adicionar(
            compradorSalvo,
            deps,
            obterClientesAtualizados,
            obterCompradoresAtualizados,
            obterProcuradoresAtualizados,
          )
    listaCmd.executar()
    handleCloseStepsComprador()
  }

  const handlePrevStep = () => setValue('step', currentStep - 1)
  const handleNextStep = async () => {
    const isValid = await validateStep('CadastroCompradoresStep')

    if (!isValid) {
      if (errors.cadastroCompradores?.message) {
        showToast({
          message: t('proposta:cadastro_compradores.erro.obrigatorio'),
          variant: 'error',
        })
      }
      return
    }
    setValue('step', currentStep + 1)
  }

  const opcoes = useCallback(() => {
    return obterOpcoesCadastroComprador(compradoresComum)
  }, [compradoresComum])

  const emailsJaVinculados = transformarClienteParaEmailDocumento(clientes)

  if (flowKey === 'PessoaFisica') {
    return (
      <CadastroCompradorPF
        nacionalidades={nacionalidades}
        profissoes={profissoes}
        clientes={clientes}
        compradores={compradoresComum}
        clienteAtendimento={clienteAtendimento}
        clienteToEdit={clienteToEdit}
        mode={mode}
        onCancel={handleCloseStepsComprador}
        onSubmit={handleSubmit}
        emailsJaVinculados={emailsJaVinculados}
      />
    )
  }

  if (flowKey === 'PessoaJuridica') {
    return (
      <CadastroCompradorPJ
        compradores={compradoresComum}
        nacionalidades={nacionalidades}
        profissoes={profissoes}
        clienteJuridicoToEdit={clienteToEdit}
        mode={mode}
        onCancel={handleCloseStepsComprador}
        onSubmit={handleSubmit}
        emailsJaVinculados={emailsJaVinculados}
      />
    )
  }

  if (flowKey === 'Procurador') {
    return (
      <CadastroProcuradorFluxoIndependente
        procuradorToEdit={clienteToEdit}
        compradores={compradoresComum}
        clientes={clientes}
        procuradores={procuradores}
        nacionalidades={nacionalidades}
        profissoes={profissoes}
        onCancel={handleCloseStepsComprador}
        onSubmit={handleSubmit}
        emailsJaVinculados={emailsJaVinculados}
      />
    )
  }

  if (flowKey === 'MaePai') {
    return (
      <CadastroMaePaiFluxoIndependente
        emailsJaVinculados={emailsJaVinculados}
        clienteToEdit={clienteToEdit}
        nacionalidades={nacionalidades}
        profissoes={profissoes}
        compradores={compradoresComum}
        clientes={clientes}
        onCancel={handleCloseStepsComprador}
        onSubmit={handleSubmit}
      />
    )
  }

  if (flowKey === 'Representante') {
    return (
      <CadastroRepresentanteLegalFluxoIndependente
        representanteLegalToEdit={clienteToEdit}
        nacionalidades={nacionalidades}
        profissoes={profissoes}
        compradores={compradoresComum}
        clientesJuridicos={clientesJuridicos}
        onCancel={handleCloseStepsComprador}
        onSubmit={handleSubmit}
      />
    )
  }

  const cadastrarCompradorButton = (isMobile = false) => (
    <SelectPopover<PersonType>
      title={t('proposta:cadastro_compradores.popover.titulo')}
      options={opcoes()}
      onSelect={openCreate}
      triggerButton={
        <Button
          variant={isEmpty ? 'default' : 'outline'}
          className={isMobile ? 'w-full' : 'w-auto'}
          data-testid={isMobile ? 'mobile-cadastrar-comprador' : 'btn-cadastrar-comprador'}
        >
          <Icon name="plus" />
          {t(
            isEmpty
              ? 'proposta:cadastro_compradores.cadastrar_comprador'
              : 'proposta:cadastro_compradores.adicionar_novo',
          )}
        </Button>
      }
      className={isMobile ? 'w-full' : 'w-75'}
    />
  )

  return (
    <StepPropostaRoot>
      <StepPropostaHeader>
        <StepPropostaHeaderContent>
          <StepPropostaHeaderTitle className="text-left">
            {t('proposta:cadastro_compradores.titulo')}
          </StepPropostaHeaderTitle>
          <StepPropostaHeaderDescription>
            {t(
              isEmpty
                ? 'proposta:cadastro_compradores.subtitulo_inicial'
                : 'proposta:cadastro_compradores.subtitulo_adicionar',
            )}
          </StepPropostaHeaderDescription>
        </StepPropostaHeaderContent>
        <StepPropostaHeaderAction className="hidden md:block">
          {cadastrarCompradorButton(false)}
        </StepPropostaHeaderAction>
      </StepPropostaHeader>
      <StepPropostaContent>
        <Render.If
          condition={!isEmpty}
          fallback={
            <>
              <CadastroCompradoresEmpty />
              <div className="-mt-12 w-full md:hidden">{cadastrarCompradorButton(true)}</div>
            </>
          }
        >
          {() => (
            <>
              <div className="mb-4 w-full md:hidden">{cadastrarCompradorButton(true)}</div>
              {compradoresComum.map((comprador, i) => (
                <CardComprador key={comprador.id}>
                  <CardCompradorHeader onDelete={() => handleOnExcluirComprador(comprador.id)}>
                    {t('proposta:cadastro_compradores.comprador', { numero: i + 1 })}
                  </CardCompradorHeader>
                  <CardCompradorContent onEdit={() => handleEdit(comprador, comprador)}>
                    <CardCompradorInfo
                      imagem={null}
                      tipo={t('proposta:cadastro_compradores.tipo.comprador')}
                      nome={comprador.nome}
                    />
                  </CardCompradorContent>
                  <ListaProcuradores
                    onEdit={(cliente) => handleEdit(cliente, comprador)}
                    procuradores={obterProcuradoresDoComprador(comprador.id)}
                  />
                  <ListaVinculosComprador
                    onEdit={(cliente) => handleEdit(cliente, comprador)}
                    vinculos={comprador.vinculos}
                    obterProcuradoresDeCompradores={obterProcuradoresDoComprador}
                  />
                </CardComprador>
              ))}
            </>
          )}
        </Render.If>
      </StepPropostaContent>
      <StepPropostaFooter>
        <Button className="font-nunito h-12 w-full md:w-auto" variant="outline" onClick={handlePrevStep}>
          {t('proposta:etapa_anterior')}
        </Button>
        <Button className="font-nunito h-12 w-full md:w-auto" onClick={handleNextStep}>
          {t('proposta:proxima_etapa')}
        </Button>
      </StepPropostaFooter>
    </StepPropostaRoot>
  )
}
